import type { ConfigValueResponse } from '@dte/otw-azure-functions/src/common/interfaces/containers/config/ConfigDocument';
import { createContext, type ReactNode, useCallback, useContext, useState } from 'react';
import { AuthenticatedJsonContent } from '../../app/(client)/(authenticated)/content/jsonGet/AuthenticatedJsonContent';
import { useAuthenticatedJsonUpdate } from '../../common/util/services/useAuthenticatedJsonUpdate';
import { ValidatedForm } from '../../components/form/ValidatedForm';
import { Spinner } from '../../components/Spinner/Spinner';

export interface ConfigValueContextProps<T> {
	id: string;
	value?: T;
	originalValue?: T;
	setValue?: (newValue: T | undefined) => void;
	refresh?: () => void;
	save?: () => Promise<void>;
}

const ConfigValueContext = createContext<ConfigValueContextProps<unknown>>({} as ConfigValueContextProps<unknown>);

// TODO: start passing type from consumers
export function useConfigValueContext<T = unknown>(): ConfigValueContextProps<T> {
	return useContext(ConfigValueContext) as ConfigValueContextProps<T>;
}

export function useLookupValueFromConfigValueContext<T>(key: string): T | undefined {
	const configValue = useConfigValueContext<Record<string, T>>();
	return configValue?.value?.[key];
}
interface Props {
	configValueId: string;
	external?: boolean;
	children?: ReactNode;
	className?: string;
}

function getConfigValueURL(configValueId: string, external: boolean): string {
	const url = '/authenticated/config';

	// TODO: this should have some validation to ensure that values are being passed in

	// TODO: make sure we're not passing both external and lookupId

	// Path for external config values
	if (external) {
		return `${url}/external`;
	}

	// Path for internal values
	return `${url}/values/${configValueId}`;
}

/**
 * Wrapper used for updating config values
 *
 * @param props
 * @returns
 */
export function UpdateConfigValueWrapper<T>(props: Props): ReactNode {
	const url = getConfigValueURL(props.configValueId, props.external);

	const [originalValue, setOriginalValue] = useState<ConfigValueResponse<T>>();
	const [rawValue, setRawValue] = useState<ConfigValueResponse<T>>();

	const updateOriginalValue = useCallback((newValue: ConfigValueResponse<T>) => {
		setOriginalValue(newValue);
		setRawValue(newValue);
	}, []);

	const updatedValue = rawValue?.value;
	const setUpdatedValue = useCallback(
		(newValue: T) => {
			if (rawValue?.value === newValue) {
				return;
			}
			setRawValue({ ...rawValue, value: newValue });
		},
		[rawValue],
	);

	const [saving, setSaving] = useState(false);
	const { updater } = useAuthenticatedJsonUpdate(url);

	const save = useCallback(async () => {
		setSaving(true);

		// Save new mode
		const postData = { value: updatedValue };
		await updater(postData);

		setSaving(false);
	}, [updatedValue, updater]);

	const context: ConfigValueContextProps<T> = {
		id: props.configValueId,
		value: updatedValue,
		originalValue: originalValue?.value,
		setValue: setUpdatedValue,
		save: save,
	};

	let content: ReactNode;
	if (saving) {
		content = <Spinner />;
	} else {
		content = (
			<ConfigValueContext value={context as ConfigValueContextProps<unknown>}>
				{props.children}
			</ConfigValueContext>
		);
	}

	return (
		<ValidatedForm>
			<AuthenticatedJsonContent url={url} setData={updateOriginalValue} hasData={originalValue !== undefined}>
				{content}
			</AuthenticatedJsonContent>
		</ValidatedForm>
	);
}

/**
 * Wrapper used for read-only display of config values
 *
 * @param props
 * @returns
 */
export function ConfigValueWrapper<T>(props: Props): ReactNode {
	const url = getConfigValueURL(props.configValueId, props.external);

	const [originalValue, setOriginalValue] = useState<ConfigValueResponse<T>>();

	const context: ConfigValueContextProps<T> = {
		id: props.configValueId,
		value: originalValue?.value,
		originalValue: originalValue?.value,
		setValue: () => {},
		save: async () => {},
	};

	return (
		<AuthenticatedJsonContent url={url} setData={setOriginalValue} hasData={originalValue !== undefined}>
			<ConfigValueContext value={context as ConfigValueContextProps<unknown>}>
				{props.children}
			</ConfigValueContext>
		</AuthenticatedJsonContent>
	);
}
